/**
 * Phone-required gate.
 *
 * Renders a non-dismissable modal whenever the authenticated cabinet customer
 * has an empty `phone` field. Used to catch users who signed up via Google
 * OAuth (no phone collected) and force them to provide a Georgian phone
 * number before they continue using the cabinet.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PhoneInput, { isPhoneValid } from "@/cabinet/components/PhoneInput";
import { useI18n } from "@/cabinet/i18n";
import { useAuth } from "@/cabinet/context/AuthContext";
import { api, formatApiError } from "@/cabinet/lib/api";

export default function PhoneRequiredModal() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  // Only render when a logged-in customer has no phone on file.
  if (!user || user.role !== "customer") return null;
  if (user.phone && user.phone.trim()) return null;

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!isPhoneValid(phone)) {
      toast.error(t("phoneGate.invalid") || "Please enter a valid Georgian phone number");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/users/me", { phone: phone.trim() });
      if (typeof refresh === "function") {
        await refresh();
      }
      toast.success(t("phoneGate.saved") || "Thanks! Phone number saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md bg-paper [&>button.absolute]:hidden"
        data-testid="phone-required-modal"
        // Disable close-on-overlay-click and esc — phone is mandatory.
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <DialogTitle className="font-display text-2xl text-center">
            {t("phoneGate.title") || "One more step"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("phoneGate.subtitle") ||
              "We need your phone number so we can reach you about deliveries and consultations."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="phone-gate-input">
              {t("phoneGate.field_label") || "Phone number"}{" "}
              <span className="text-xs text-[hsl(var(--destructive))]">*</span>
            </Label>
            <PhoneInput
              value={phone}
              onChange={setPhone}
              testId="phone-gate-input"
            />
            <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
              {t("phoneGate.hint") ||
                "Georgian mobile number — we'll only use it for important order updates."}
            </p>
          </div>

          <Button
            type="submit"
            disabled={saving || !isPhoneValid(phone)}
            data-testid="phone-gate-save"
            className="w-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-white"
          >
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {t("phoneGate.save") || "Save and continue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
